import type { EditOp, ResolvedEdit } from "../contracts/edit-graph.js";
import type { Milli } from "../contracts/ids.js";
import type {
  LookTreatment,
  SfxOption,
  SourcePack,
} from "../contracts/source-pack.js";
import { addMilli, clampMilli, mulMilli } from "../scoring/fixed.js";

/**
 * Bounded presentation scoring from resolved creative ops only.
 *
 * Store ownership / unlocks are intentionally absent from inputs — scoring
 * never reads purchases. Catalog `presentationReachMilli` is not applied as a
 * flat item-ID Reach bonus (that would become pay-to-win when options are
 * store-gated). Instead:
 * - each active creative lane contributes the same production sidegrade
 * - intensity comes from semantic tags (expressive / urgent / handheld / …)
 * - combination quality adds a small bounded bonus for using multiple lanes
 *
 * Cleared / superseded history is already absent from ResolvedEdit.
 * Neutral authored Clean/Steady/Hard Cut still occupy a lane when selected;
 * explicit clear ops leave the lane empty and award nothing.
 */
const LANE_PRODUCTION_MILLI = 28;
const COMBINATION_BONUS_PER_EXTRA_LANE_MILLI = 12;

export function creativePresentationScoreBoost(
  pack: SourcePack,
  resolved: ResolvedEdit,
): {
  intensityMilli: Milli;
  productionMilli: Milli;
  /** Always 0 — creative Reach must not be a flat catalog-ID bonus. */
  presentationReachMilli: Milli;
  audioSyncMilli: Milli;
  claimDriftMilli: Milli;
} {
  let intensityMilli: Milli = 0;
  let productionMilli: Milli = 0;
  let audioSyncMilli: Milli = 0;
  let claimDriftMilli: Milli = 0;
  let activeLanes = 0;

  const lookById = indexById(pack.lookTreatments);
  const stickerById = indexById(pack.stickerOptions);
  const sfxOptionByAsset = indexSfxByAsset(pack.sfxOptions);
  const assetById = new Map(pack.assets.map((a) => [a.id, a]));

  if (resolved.look?.treatmentId) {
    activeLanes += 1;
    productionMilli = addMilli(productionMilli, LANE_PRODUCTION_MILLI);
    const treatment = lookById.get(resolved.look.treatmentId);
    if (treatment) {
      const scale = intensityScale(treatment, resolved.look.intensityMilli);
      intensityMilli = addMilli(
        intensityMilli,
        mulMilli(lookIntensityFromTags(treatment), scale),
      );
    }
  }

  // Motion (camera moves) no longer contributes to the score: it is now a
  // presentation-only TIMED EFFECT placed in the Look tool, and its emotional
  // read rides the landing model (`emotion-model.ts` `levers.motions`), not the
  // core headline. The `motion` op still exists in the graph (it drives the
  // camera-move preview), it simply opens no scoring lane here.

  if (resolved.stickers.length > 0) {
    activeLanes += 1;
    productionMilli = addMilli(productionMilli, LANE_PRODUCTION_MILLI);
    for (const stickerOp of resolved.stickers) {
      const sticker = stickerById.get(stickerOp.stickerId);
      if (sticker?.tags.includes("hype") || sticker?.tags.includes("energy")) {
        intensityMilli = addMilli(intensityMilli, 15);
      }
    }
  }

  if (resolved.transitions.size > 0) {
    activeLanes += 1;
    productionMilli = addMilli(productionMilli, LANE_PRODUCTION_MILLI);
    for (const transitionOp of resolved.transitions.values()) {
      if (!transitionOp.presetId) continue;
      const preset = (pack.transitionPresets ?? []).find(
        (t) => t.id === transitionOp.presetId,
      );
      if (preset?.tags.includes("urgent") || preset?.tags.includes("flash")) {
        intensityMilli = addMilli(intensityMilli, 12);
      }
    }
  }

  if (resolved.sfx.length > 0) {
    activeLanes += 1;
    productionMilli = addMilli(productionMilli, LANE_PRODUCTION_MILLI);
    // Count-insensitive (a creative layer, like stickers): the SFX lane
    // contributes ONCE regardless of how many sounds are placed, so stacking
    // sounds never stacks the score. Volume still matters — each effect scales
    // by the LOUDEST placement of that kind, not the sum across instances.
    let maxVolume = 0;
    let ominousVolume = 0;
    let positiveVolume = 0;
    for (const sfxInst of resolved.sfx) {
      const option = sfxOptionByAsset.get(sfxInst.assetId);
      const asset = assetById.get(sfxInst.assetId);
      const tags = option?.tags ?? asset?.tags ?? [];
      const v = sfxInst.volumeMilli;
      if (v > maxVolume) maxVolume = v;
      if (tags.includes("ominous") || tags.includes("alarm")) {
        if (v > ominousVolume) ominousVolume = v;
      } else if (tags.includes("positive") || tags.includes("success")) {
        if (v > positiveVolume) positiveVolume = v;
      }
    }
    audioSyncMilli = addMilli(audioSyncMilli, mulMilli(60, maxVolume));
    if (ominousVolume > 0) {
      intensityMilli = addMilli(intensityMilli, mulMilli(80, ominousVolume));
      claimDriftMilli = addMilli(claimDriftMilli, mulMilli(40, ominousVolume));
    }
    if (positiveVolume > 0) {
      intensityMilli = addMilli(intensityMilli, mulMilli(20, positiveVolume));
    }
  }

  if (activeLanes > 1) {
    productionMilli = addMilli(
      productionMilli,
      (activeLanes - 1) * COMBINATION_BONUS_PER_EXTRA_LANE_MILLI,
    );
  }

  return {
    intensityMilli: clampMilli(intensityMilli, 0, 200),
    productionMilli: clampMilli(productionMilli, 0, 250),
    presentationReachMilli: 0,
    audioSyncMilli: clampMilli(audioSyncMilli, 0, 150),
    claimDriftMilli: clampMilli(claimDriftMilli, 0, 120),
  };
}

function indexById<T extends { id: string }>(
  list: readonly T[] | undefined,
): Map<string, T> {
  return new Map((list ?? []).map((item) => [item.id, item]));
}

function indexSfxByAsset(
  list: readonly SfxOption[] | undefined,
): Map<string, SfxOption> {
  const map = new Map<string, SfxOption>();
  for (const option of list ?? []) {
    if (option.assetId) map.set(option.assetId, option);
  }
  return map;
}

function intensityScale(treatment: LookTreatment, intensityMilli: Milli | undefined): Milli {
  if (!treatment.intensitySupported) return 1000;
  const intensity = intensityMilli ?? treatment.defaultIntensityMilli;
  return clampMilli(intensity, 0, 1000);
}

function lookIntensityFromTags(treatment: LookTreatment): Milli {
  if (treatment.tags.includes("expressive") || treatment.tags.includes("urgent")) {
    return 40;
  }
  if (treatment.tags.includes("clean") || treatment.id === "look-clean") {
    return 0;
  }
  return 20;
}

/** Active look op after last-wins resolution (null when none). */
export type ResolvedLookOp = Extract<EditOp, { kind: "look" }> | null;
